import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { getCycleKey, getPondKey } from 'common/constants/queryKeys'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import useCycleApi from 'common/hooks/useCycleApi'
import { useGetPonds } from 'modules/pond/api/queries'
import { useSpeciesName } from 'modules/pond/api/selector'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { normalizeSpeciesName } from 'utils/analytics'

import { TRANSFER_REQUEST_CONFIG } from '.'
import { useGetShrimpTransfer } from './queries'
import { CreateShrimpTransferVariables, DeleteShrimpTransferVariables } from './types'

// Prefixes of queries whose numbers change when shrimp move between ponds.
const TRANSFER_AFFECTED_KEY_PREFIXES = [
  'pond-dashboard',
  'pond-dashboard-chart',
  'pond-dashboard-revenue-chart',
  'org-yield-sizes-current',
  'org-yield-sizes-estimate',
  'org-yield-size-ponds',
  'org-yield-harvest',
]
// Backend recalculates CycleYieldSummary for both ponds in the background
// (DOF-1344), so refetch once more after it has had time to finish.
const RECALCULATION_DELAY_MS = 3000

/**
 * Refetch pond dashboards (source + destination), cycle history and the org
 * yield sections right after a transfer is created / edited / deleted, so
 * the user doesn't have to refresh manually (DOF-1344).
 */
const useRefetchTransferAffectedQueries = () => {
  const queryClient = useQueryClient()

  return () => {
    const refetch = () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          TRANSFER_AFFECTED_KEY_PREFIXES.includes(String(query.queryKey[0])),
      })
      queryClient.invalidateQueries({ queryKey: getCycleKey.all() })
    }
    refetch()
    setTimeout(refetch, RECALCULATION_DELAY_MS)
  }
}

/**
 * A transfer can land in another farm (DOF-1368): refresh the empty pond
 * lists, pond list/names/filter counts of both the source (route) farm and
 * the destination farm, plus the destination pond's cycles.
 */
const useInvalidateDestinationFarm = () => {
  const queryClient = useQueryClient()
  const { farmId } = useParams()

  return (destinationFarmId?: string, destinationPondId?: string) => {
    // Empty ponds of every farm change (source + destination), refresh all.
    queryClient.invalidateQueries({ queryKey: [...getPondKey.all(), 'completed'] })
    const targetFarmId = destinationFarmId || farmId
    const farmIds = Array.from(new Set([farmId, targetFarmId].filter(Boolean))) as string[]
    farmIds.forEach((id) => {
      queryClient.invalidateQueries({ queryKey: getPondKey.list(id) })
      queryClient.invalidateQueries({ queryKey: getPondKey.filter(id) })
      queryClient.invalidateQueries({ queryKey: getPondKey.filterCount(id) })
      queryClient.invalidateQueries({ queryKey: getPondKey.names(id) })
    })
    if (targetFarmId && destinationPondId) {
      queryClient.invalidateQueries({
        queryKey: getCycleKey.byFarmByPond(targetFarmId, destinationPondId),
      })
    }
  }
}

export const useMutateCreateShrimpTransfer = () => {
  const cycleApi = useCycleApi()
  const { enqueueSnackbar } = useSnackbar()
  const { refetch } = useGetShrimpTransfer()
  const { refetch: refetchPonds } = useGetPonds()
  const { t } = useTranslation(['common'])
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()
  const refetchTransferAffected = useRefetchTransferAffectedQueries()
  const invalidateDestinationFarm = useInvalidateDestinationFarm()

  return useMutation(
    ({ body: data }: CreateShrimpTransferVariables) =>
      // The form maps every business error code itself (400/403/404), so the
      // interceptor must not add its own forbidden snackbar on top.
      cycleApi.request({ ...TRANSFER_REQUEST_CONFIG.create, data, silentError: true }),
    {
      onSuccess: (_data, { body, destinationFarmId }) => {
        enqueueSnackbar(t('common:create.success'), { variant: 'success' })
        refetch()
        refetchPonds()
        refetchTransferAffected()
        invalidateDestinationFarm(destinationFarmId, body.destination_pond_id)
        // Backend moves shrimp between both ponds in this single call — fire
        // exactly once here, never per-pond.
        const normalizedSpecies = normalizeSpeciesName(speciesName)
        trackEvent(
          AnalyticsEvents.transferRecordCreated,
          normalizedSpecies ? { species: normalizedSpecies } : undefined
        )
      },
    }
  )
}

export const useMutateDeleteShrimpTransfer = () => {
  const cycleApi = useCycleApi()
  const { enqueueSnackbar } = useSnackbar()
  const { refetch } = useGetShrimpTransfer()
  const { refetch: refetchPonds } = useGetPonds()
  const { t } = useTranslation(['common'])
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()
  const refetchTransferAffected = useRefetchTransferAffectedQueries()
  const invalidateDestinationFarm = useInvalidateDestinationFarm()

  return useMutation(
    ({ partialTransferId }: DeleteShrimpTransferVariables) =>
      cycleApi.request({
        ...TRANSFER_REQUEST_CONFIG.delete,
        url: `${TRANSFER_REQUEST_CONFIG.delete.url}/${partialTransferId}`,
      }),
    {
      onSuccess: (_data, { destinationFarmId, destinationPondId }) => {
        enqueueSnackbar(t('common:delete.success'), { variant: 'success' })
        refetch()
        refetchPonds()
        refetchTransferAffected()
        invalidateDestinationFarm(destinationFarmId, destinationPondId)
        const normalizedSpecies = normalizeSpeciesName(speciesName)
        trackEvent(
          AnalyticsEvents.transferRecordDeleted,
          normalizedSpecies ? { species: normalizedSpecies } : undefined
        )
      },
      onError: (error) => {
        // The interceptor stays silent on 400, so report failures here.
        if (axios.isAxiosError(error) && error.response?.status === 400) {
          enqueueSnackbar(t('common:error.generic'), { variant: 'error' })
        }
      },
    }
  )
}
